import { FieldType, isEmptyOrWhitespace, isNullOrUndefined, ViewModelBase } from "@shoothill/core";
import type { ValidationResponse } from "@shoothill/core";
import { action, computed, IComputedValue, observable, runInAction } from "mobx";

import { AppUrls } from "AppUrls";
import { ClientViewModel } from "Globals/ViewModels/ClientViewModel";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { OrderEditLineModel } from "./OrderEditLineModel";
import { CategoryModel } from "../CategoryModel";
import { LineDescriptionModel } from "../LineDescriptionModel";
import { SubCategoryModel } from "../SubCategoryModel";
import { OrderType } from "Globals/Models/Domain";
import { OrderTypeModel } from "../OrderTypeModel";

export class OrderEditLineViewModel extends ViewModelBase<OrderEditLineModel> {
    // #region Constructors and Disposers

    constructor(
        activeIEId: IComputedValue<string | null>,
        activeSupplierId: IComputedValue<string | null>,
        isCentral: IComputedValue<boolean>,
        prePaymentCategoryNames: IComputedValue<string[]>,
        prePaymentSubCategoryIds: IComputedValue<string[]>,
        prePaymentDescriptionNames: IComputedValue<string[]>,
        addOrderLineCallbackAsync: (orderLineModel: OrderEditLineModel) => Promise<boolean>,
        orderType: IComputedValue<OrderTypeModel | null>,
        isHireLegacy: IComputedValue<boolean>,
    ) {
        super(new OrderEditLineModel());

        this.activeIEId = activeIEId;
        this.activeSupplierId = activeSupplierId;
        this.isCentral = isCentral;
        this.prePaymentCategoryNames = prePaymentCategoryNames;
        this.prePaymentSubCategoryNames = prePaymentSubCategoryIds;
        this.prePaymentDescriptionNames = prePaymentDescriptionNames;
        this.addOrderLineCallbackAsync = addOrderLineCallbackAsync;
        this.orderType = orderType;
        this.isHireLegacy = isHireLegacy;

        this.setDecorators(OrderEditLineModel);
    }

    // #endregion Constructors and Disposers

    // #region: misc stuff from IEItemLine

    @observable
    public fromIEItemLine = false;

    private prePaymentCategoryNames: IComputedValue<string[]>;

    private prePaymentSubCategoryNames: IComputedValue<string[]>;

    private prePaymentDescriptionNames: IComputedValue<string[]>;

    // Category

    @observable
    public categoryIdFromIEItem = "";

    @observable
    public categoryNameFromIEItem = "";

    @action
    public setCategoryFromIEItem = (id: string, name: string | undefined) => {
        this.categoryIdFromIEItem = id;
        this.categoryNameFromIEItem = name != undefined ? name : OrderEditLineModel.DEFAULT_CATEGORYDISPLAYNAME;
    };

    // Subcategory

    @observable
    public subcategoryIdFromIEItem = "";

    @observable
    public subcategoryNameFromIEItem = "";

    @action
    public setSubcategoryFromIEItem = (id: string, name: string | undefined) => {
        this.subcategoryIdFromIEItem = id;
        this.subcategoryNameFromIEItem = name != undefined ? name : OrderEditLineModel.DEFAULT_SUBCATEGORYDISPLAYNAME;
    };

    // Line description

    @observable
    public lineDescriptionIdFromIEItem = "";

    @observable
    public lineDescriptionNameFromIEItem = "";

    @action
    public setDescriptionFromIEItem = (id: string, name: string | undefined) => {
        this.lineDescriptionIdFromIEItem = id;
        this.lineDescriptionNameFromIEItem = name != undefined ? name : OrderEditLineModel.DEFAULT_LINEDESCRIPTIONDISPLAYNAME;
    };

    // #endregion: misc stuff from IEItemLine

    // Category

    // #region Properties

    private activeIEId: IComputedValue<string | null>;

    private activeSupplierId: IComputedValue<string | null>;

    private isCentral: IComputedValue<boolean>;

    private orderType: IComputedValue<OrderTypeModel | null>;

    private isHireLegacy: IComputedValue<boolean>;

    public client = new ClientViewModel();

    public server = new ServerViewModel();

    /**
     * Determines the configuration of the orderline form.
     */
    @computed
    public get isStockOrderLine() {
        return this.category?.displayName === "Stock";
    }

    // #endregion Properties

    // #region Categories

    @observable
    public categories = observable<CategoryModel>([]);

    @computed
    public get category() {
        return this.categories.find((p) => p.id === this.model.categoryId) ?? null;
    }

    @action
    public setCategoryAsync = async (value: CategoryModel | null) => {
        this.model.categoryId = value?.id ?? OrderEditLineModel.DEFAULT_CATEGORYID;
        this.model.categoryDisplayName = value ? value.displayName : OrderEditLineModel.DEFAULT_CATEGORYDISPLAYNAME;
        this.model.variationId = value?.variationId ?? OrderEditLineModel.DEFAULT_VARIATIONID;

        // SIDE-EFFECT.
        // Having set the category, we need to see if we have the sub-categories available
        // locally and if not load them from the server.
        switch (true) {
            case !isNullOrUndefined(value):
                await this.setSubCategoriesAsync(value!.id);
                await this.setSubCategoryAsync(OrderEditLineModel.DEFAULT_SUBCATEGORYID);
                break;

            default:
                await this.setSubCategoryAsync(OrderEditLineModel.DEFAULT_SUBCATEGORYID);
                break;
        }
    };

    @computed
    private get validateCategory(): ValidationResponse {
        const errorMessage = this.model.validateCategoryId;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    public get categoriesForIEAndVariations(): CategoryModel[] {
        if (this.activeIEId.get()) {
            if (this.isCentral.get()) {
                const cats = this.categories.filter((c) => c.incomeAndExpenditureId === this.activeIEId.get());
                return cats;
            }
        }

        return [];
    }

    public setCategoriesAsync = async (incomeAndExpenditureId: string) => {
        if (this.categories.findIndex((cat) => cat.incomeAndExpenditureId === incomeAndExpenditureId) === -1) {
            return this.loadCategoriesAsync();
        }
    };

    // #endregion Categories

    // #region Sub-categories

    @observable
    public subCategories = observable<SubCategoryModel>([]);

    @computed
    public get subCategory() {
        return this.subCategories.find((p) => p.id === this.model.subCategoryId) ?? null;
    }

    @action
    public setSubCategoryAsync = async (value: SubCategoryModel | null) => {
        this.model.subCategoryId = value?.id ?? OrderEditLineModel.DEFAULT_SUBCATEGORYID;
        this.model.subCategoryDisplayName = value ? value.displayName : OrderEditLineModel.DEFAULT_SUBCATEGORYDISPLAYNAME;

        // SIDE-EFFECT.
        // Having set the sub-category, we need to see if we have the line description available
        // Locally and if not load from the server.
        switch (true) {
            case !isNullOrUndefined(value):
                await this.setLineDescriptionsAsync(value!.id);
                this.setLineDescription(OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID);
                break;

            default:
                this.setLineDescription(OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID);
                break;
        }
    };

    @computed
    private get validateSubCategory(): ValidationResponse {
        const errorMessage = this.model.validateSubCategoryId;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    public get subCategoriesForCategory(): SubCategoryModel[] {
        if (this.isCentral.get()) {
            const subcats = this.subCategories.filter((sc) => sc.categoryId === this.model.categoryId);
            return subcats;
        }
        return this.subCategories.filter((sc) => sc.categoryId === this.model.categoryId);
    }

    public setSubCategoriesAsync = async (categoryId: string) => {
        if (this.subCategories.findIndex((subCat) => subCat.categoryId === categoryId) === -1) {
            return this.loadSubCategoriesAsync();
        }
    };

    // #endregion Sub-categories

    // #region Line descriptions

    @observable
    public lineDescriptions = observable<LineDescriptionModel>([]);

    @computed
    public get lineDescription() {
        return this.lineDescriptions.find((p) => p.id === this.model.lineDescriptionId) ?? null;
    }

    @action
    public setLineDescription = (value: LineDescriptionModel | null) => {
        this.model.lineDescriptionId = value ? value.id : OrderEditLineModel.DEFAULT_LINEDESCRIPTIONID;
        this.model.lineDescriptionDisplayName = value ? value.displayName : OrderEditLineModel.DEFAULT_LINEDESCRIPTIONDISPLAYNAME;
    };

    @computed
    private get validateLineDescription(): ValidationResponse {
        const errorMessage = this.model.validateLineDescriptionId;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    public get lineDescriptionsForSubCategory(): LineDescriptionModel[] {
        if (this.isCentral.get()) {
            const lines = this.lineDescriptions.filter((ld) => ld.subCategoryId === this.model.subCategoryId);
            return lines;
        }
        return this.lineDescriptions.filter((ld) => ld.subCategoryId === this.model.subCategoryId);
    }

    public setLineDescriptionsAsync = async (subCategoryId: string) => {
        if (this.lineDescriptions.findIndex((ld) => ld.subCategoryId === subCategoryId) === -1) {
            return this.loadLineDescriptionsAsync();
        }
    };

    // #endregion Line descriptions

    // #region Dates for Hire Order Type

    @computed
    private get validateFromDate(): ValidationResponse {
        // RULES:
        // 1. Date is required if order type is hire
        // 2. Unless `isNA` is true (then date isn't required)

        const isHireOrder = this.orderType?.get()?.type === OrderType.Hire;

        const isDefaultDate = this.model.dateFrom === OrderEditLineModel.DEFAULT_DATEFROM;

        let errorMessage = "";

        if (this.isHireLegacy.get() === true) {
            errorMessage = ""; // Handled at the PO level.
        } else {
            if (isHireOrder && !this.model.isNA && isDefaultDate) {
                errorMessage = "Please provide a date";
            }
        }

        return {
            errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateToDate(): ValidationResponse {
        // RULES:
        // 1. Date is required if order type is hire
        // 2. Unless `isNA` is true (then date isn't required)

        const isHireOrder = this.orderType?.get()?.type === OrderType.Hire;

        const isDefaultDate = this.model.dateTo === OrderEditLineModel.DEFAULT_DATETO;

        let errorMessage = "";

        if (this.isHireLegacy.get() === true) {
            errorMessage = ""; // Handled at the PO level.
        } else {
            if (isHireOrder && !this.model.isNA && isDefaultDate) {
                errorMessage = "Please provide a date";
            }
        }

        return {
            errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #endregion Dates for Hire Order Type

    // #region Client Actions

    @observable
    private addOrderLineCallbackAsync: (orderLineModel: OrderEditLineModel) => Promise<boolean>;

    @action
    public add = () => {
        this.client.command(
            () => {
                return true;
            },
            async () => {
                await this.addOrderLineCallbackAsync(this.model);
            },
            this.isModelValid,
            "There was an error trying to edit the order line",
        );
    };

    @action
    public reset = (fullReset: boolean) => {
        this.model.reset(fullReset);
        this.client.reset();

        this.fromIEItemLine = false;
    };

    // #endregion Client Actions

    // #region Server Actions

    public loadCategoriesAsync = async () => {
        return this.server.query<any>(
            () => this.Get(`${AppUrls.Server.PurchaseOrder.GetIECategoryByIEId}\\${this.activeIEId}`),
            (result: any) => {
                runInAction(() => {
                    this.categories.push(...CategoryModel.fromDtos(result));
                });
            },
        );
    };

    public loadSubCategoriesAsync = async () => {
        return this.server.query<any>(
            () => this.Get(`${AppUrls.Server.PurchaseOrder.GetIESubcategoryByCatId}\\${this.model.categoryId}`),
            (result: any) => {
                runInAction(() => {
                    this.subCategories.push(...SubCategoryModel.fromDtos(result));
                });
            },
        );
    };

    public loadLineDescriptionsAsync = async () => {
        return this.server.query<any>(
            () => this.Get(`${AppUrls.Server.PurchaseOrder.GetIELineItemsBySubcategoryId}\\${this.model.subCategoryId}`),
            (result: any) => {
                runInAction(() => {
                    this.lineDescriptions.push(...LineDescriptionModel.fromDtos(result));
                });
            },
        );
    };

    // #endregion Server Actions

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<OrderEditLineModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (this.client.IsSubmitted) {
            // Process the properties of the model that cannot be supported via
            // the use of decorators.
            switch (fieldName) {
                case "categoryId": {
                    const result = this.validateCategory;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                case "subCategoryId": {
                    const result = this.validateSubCategory;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                case "lineDescriptionId": {
                    const result = this.validateLineDescription;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                case "dateFrom": {
                    const result = this.validateFromDate;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }

                case "dateTo": {
                    const result = this.validateToDate;

                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
            }
        } else {
            // Do not validate if the properties of the model have not been
            // submitted.
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boilerplate
}
